import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, logout } from '../lib/adminAPI';
import { ArrowLeft, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useRowSelection, DeleteSelectedBar } from '../components/admin/RowSelection';

const AdminAgents = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { selected, toggle, toggleAll, clear } = useRowSelection();

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      const data = await adminAPI.getAgents();
      setAgents(data.agents);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
      }
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Agent Name', 'Email', 'Phone', 'License Number', 'Experience', 'Service Areas', 'Agency', 'Website', 'Submitted At'];
    const csvContent = [
      headers.join(','),
      ...agents.map(agent => [
        agent.agentName,
        agent.email,
        agent.phone,
        agent.licenseNumber,
        agent.yearsExperience,
        agent.serviceAreas,
        agent.agencyName || '',
        agent.website || '',
        new Date(agent.submittedAt).toLocaleString()
      ].map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agents_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('CSV exported successfully!');
  };

  const deleteSelected = async () => {
    const ids = [...selected];
    const { deleted } = await adminAPI.deleteAgents(ids);
    setAgents(prev => prev.filter(a => !selected.has(a.id)));
    clear();
    return deleted;
  };

  const filteredAgents = agents.filter(agent =>
    agent.agentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.phone.includes(searchTerm) ||
    agent.serviceAreas.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const visibleIds = filteredAgents.map(a => a.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));

  if (loading) {
    return <div className="admin-loading">Loading agents...</div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <button onClick={() => navigate('/admin/dashboard')} className="admin-back-btn">
            <ArrowLeft size={20} />
            Back to Dashboard
          </button>
          <h1 className="admin-page-title">Agent Applications</h1>
          <p className="admin-page-subtitle">{agents.length} total applications</p>
        </div>
        <div className="admin-header-actions">
          <button onClick={exportToCSV} className="admin-export-btn">
            <Download size={18} />
            Export CSV
          </button>
          <button onClick={logout} className="admin-logout-btn">
            Logout
          </button>
        </div>
      </div>

      <div className="admin-search">
        <input
          type="text"
          placeholder="Search by name, email, phone, or service areas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="admin-search-input"
        />
      </div>

      <DeleteSelectedBar count={selected.size} label="application" onConfirm={deleteSelected} onClear={clear} />

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th className="admin-check-cell">
                <input
                  type="checkbox"
                  aria-label="Select all visible applications"
                  checked={allVisibleSelected}
                  onChange={() => toggleAll(visibleIds)}
                  data-testid="select-all-agents"
                />
              </th>
              <th>Agent Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>License</th>
              <th>Experience</th>
              <th>Service Areas</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAgents.length === 0 ? (
              <tr>
                <td colSpan="9" className="admin-table-empty">
                  {searchTerm ? 'No agents match your search' : 'No agent applications yet'}
                </td>
              </tr>
            ) : (
              filteredAgents.map(agent => (
                <tr key={agent.id} className={selected.has(agent.id) ? 'admin-row-selected' : ''}>
                  <td className="admin-check-cell">
                    <input
                      type="checkbox"
                      aria-label={`Select ${agent.agentName}`}
                      checked={selected.has(agent.id)}
                      onChange={() => toggle(agent.id)}
                      data-testid={`select-agent-${agent.id}`}
                    />
                  </td>
                  <td className="admin-table-name">{agent.agentName}</td>
                  <td>{agent.email}</td>
                  <td>{agent.phone}</td>
                  <td>{agent.licenseNumber}</td>
                  <td>{agent.yearsExperience}</td>
                  <td>{agent.serviceAreas}</td>
                  <td>{new Date(agent.submittedAt).toLocaleDateString()}</td>
                  <td>
                    <button
                      onClick={() => navigate(`/admin/agents/${agent.id}`)}
                      className="admin-view-btn"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminAgents;
